import { normalizeQuery } from "@/lib/analytics/search-normalize";
import { containsSearchQueryPii } from "@/lib/analytics/search-pii-filter";
import type { PageOriginContext } from "@/lib/page-origin-context";

export const SEARCH_EVENT_PERFORMED = "search_performed";
export const SEARCH_EVENT_RESULT_CLICKED = "search_result_clicked";
export const SEARCH_EVENT_ABANDONED = "search_abandoned";

export type AbandonReason =
  | "escape"
  | "cleared"
  | "outside_click"
  | "blur_collapse"
  | "route_change";

type CaptureFn = (event: string, properties?: Record<string, unknown>) => void;

export type SearchEventContext = {
  search_session_id: string;
  rawQuery: string;
  page: string;
  page_origin: PageOriginContext | null;
  is_grownup: boolean;
};

export type SearchPerformedExtras = {
  result_count: number;
  had_results: boolean;
};

export type SearchResultClickedExtras = {
  game_id: string;
  game_slug: string;
  position: number;
  result_count: number;
};

export type SearchAbandonedExtras = {
  result_count: number;
  had_results: boolean;
  abandon_reason: AbandonReason;
};

/**
 * Builds shared search analytics properties, or `null` when the query looks like
 * PII (email, phone, SSN, card/long digit runs, URL, @handle). A `null` return is a
 * hard gate: callers must suppress the event entirely rather than emit redacted text.
 *
 * `query_length` is always derived from `normalizeQuery(rawQuery).length` so it cannot
 * diverge from the de-dupe key. The trimmed `query` text is included for all visitors
 * (kids and grownups) once it has cleared the PII gate; `is_grownup` is kept so PostHog
 * can still segment kid vs grownup searches.
 */
export function buildSearchEventProperties(
  context: SearchEventContext,
  extras: Record<string, unknown>
): Record<string, unknown> | null {
  const trimmedQuery = context.rawQuery.trim();
  if (containsSearchQueryPii(trimmedQuery)) {
    return null;
  }

  const queryLength = normalizeQuery(context.rawQuery).length;
  return {
    search_session_id: context.search_session_id,
    query_length: queryLength,
    page: context.page,
    page_origin: context.page_origin,
    is_grownup: context.is_grownup,
    query: trimmedQuery,
    ...extras,
  };
}

export function captureSearchPerformed(
  capture: CaptureFn | undefined,
  context: SearchEventContext,
  extras: SearchPerformedExtras
): void {
  if (!capture) return;
  const properties = buildSearchEventProperties(context, {
    result_count: extras.result_count,
    had_results: extras.had_results,
  });
  if (!properties) return;
  capture(SEARCH_EVENT_PERFORMED, properties);
}

export function captureSearchResultClicked(
  capture: CaptureFn | undefined,
  context: SearchEventContext,
  extras: SearchResultClickedExtras
): void {
  if (!capture) return;
  const properties = buildSearchEventProperties(context, {
    game_id: extras.game_id,
    game_slug: extras.game_slug,
    position: extras.position,
    result_count: extras.result_count,
  });
  if (!properties) return;
  capture(SEARCH_EVENT_RESULT_CLICKED, properties);
}

export function captureSearchAbandoned(
  capture: CaptureFn | undefined,
  context: SearchEventContext,
  extras: SearchAbandonedExtras
): void {
  if (!capture) return;
  const properties = buildSearchEventProperties(context, {
    result_count: extras.result_count,
    had_results: extras.had_results,
    abandon_reason: extras.abandon_reason,
  });
  if (!properties) return;
  capture(SEARCH_EVENT_ABANDONED, properties);
}
